import React, { useState, useEffect } from 'react';
import dotPattern from '../../assets/images/dot-pattern.png';
import loginVideo from '../../assets/images/login.mp4';
import maxbountyLogoSquare from '../../assets/images/MaxBounty_Square.png';
import { Link } from 'react-router-dom';
import Loading from '../../component/UI/Loading/Loading';

function formatEpc(epc) {
  const n = parseFloat(epc, 10);
  if (Number.isFinite(n) && n > 0) return `$${n.toFixed(2)}`;
  return 'New';
}

function normalizeThumbnailUrl(input) {
  if (!input || typeof input !== 'string') return null;

  const resourcesRoot = (process.env.REACT_APP_RESOURCE_URI || '').replace(/\/$/, '');
  const cdnRoot = (process.env.REACT_APP_TRACKING_CDN_URL || '').replace(/\/$/, '');

  try {
    const u = new URL(input);
    const pathname = u.pathname || '';

    if (resourcesRoot && pathname.startsWith('/creative/')) {
      return `${resourcesRoot}${pathname}${u.search || ''}`;
    }
    if (cdnRoot && pathname.startsWith('/trk/cdn/')) {
      return `${cdnRoot}${pathname.replace(/^\/trk\/cdn/, '')}${u.search || ''}`;
    }

    return input;
  } catch {
    if (resourcesRoot && input.startsWith('/creative/')) return `${resourcesRoot}${input}`;
    if (cdnRoot && input.startsWith('/trk/cdn/')) return `${cdnRoot}${input.replace(/^\/trk\/cdn/, '')}`;
    return input;
  }
}

function buildMainOptions(list) {
  const map = new Map();
  list.forEach((c) => {
    const id = c.main_category_id;
    if (!map.has(id)) {
      map.set(id, { main_category_id: id, main_category_name: c.main_category_name, count: 0 });
    }
    map.get(id).count += 1;
  });
  return Array.from(map.values()).sort((a, b) =>
    (a.main_category_name || '').localeCompare(b.main_category_name || '', undefined, { sensitivity: 'base' })
  );
}

function buildSubOptions(list, selectedMain) {
  const mainId = selectedMain === '' ? 0 : parseInt(selectedMain, 10) || 0;
  const src = mainId > 0 ? list.filter((c) => c.main_category_id === mainId) : list;
  const map = new Map();
  src.forEach((c) => {
    const id = c.subcategory_id;
    if (!map.has(id)) {
      map.set(id, { subcategory_id: id, subcategory_name: c.subcategory_name, count: 0 });
    }
    map.get(id).count += 1;
  });
  return Array.from(map.values()).sort((a, b) =>
    (a.subcategory_name || '').localeCompare(b.subcategory_name || '', undefined, { sensitivity: 'base' })
  );
}

function buildRows(list, searchTerm, selectedMain, selectedSub, sortKey, sortAsc) {
  const q = searchTerm.trim().toLowerCase();
  const mainId = selectedMain === '' ? 0 : parseInt(selectedMain, 10) || 0;
  const subId = selectedSub === '' ? 0 : parseInt(selectedSub, 10) || 0;

  let rows = list.filter((c) => {
    if (q) {
      const n = (c.name || '').toLowerCase();
      const d = (c.description || '').toLowerCase();
      if (!n.includes(q) && !d.includes(q)) return false;
    }
    if (mainId > 0 && c.main_category_id !== mainId) return false;
    if (subId > 0 && c.subcategory_id !== subId) return false;
    return true;
  });

  const dir = sortAsc ? 1 : -1;
  rows = [...rows].sort((a, b) => {
    if (sortKey === 'name') {
      return dir * (a.name || '').localeCompare(b.name || '', undefined, { sensitivity: 'base' });
    }
    if (sortKey === 'rate') {
      const va = Number.isFinite(Number(a.numeric_rate)) ? Number(a.numeric_rate) : 0;
      const vb = Number.isFinite(Number(b.numeric_rate)) ? Number(b.numeric_rate) : 0;
      if (va === vb) return 0;
      return va < vb ? -dir : dir;
    }
    if (sortKey === 'epc') {
      const va = Number.isFinite(parseFloat(a.epc, 10)) ? parseFloat(a.epc, 10) : 0;
      const vb = Number.isFinite(parseFloat(b.epc, 10)) ? parseFloat(b.epc, 10) : 0;
      if (va === vb) return 0;
      return va < vb ? -dir : dir;
    }
    return 0;
  });
  return rows;
}

const CampaignHub = () => {
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedMain, setSelectedMain] = useState('');
  const [selectedSub, setSelectedSub] = useState('');
  const [sortKey, setSortKey] = useState('name');
  const [sortAsc, setSortAsc] = useState(true);

  useEffect(() => {
    fetch(`${process.env.REACT_APP_API_URL}/campaignHub`, { method: 'GET', credentials: 'omit' })
      .then((res) => res.json())
      .then((result) => {
        if (result.success === true && Array.isArray(result.campaigns)) {
          setCampaigns(result.campaigns);
          setError(null);
        } else {
          setError((result.errors && result.errors.message) || 'Unable to load campaigns.');
        }
        setLoading(false);
      })
      .catch((err) => {
        setError(err.message || 'Connection error.');
        setLoading(false);
      });
  }, []);

  const mains = buildMainOptions(campaigns);
  const subs = buildSubOptions(campaigns, selectedMain);
  const rows = buildRows(campaigns, searchTerm, selectedMain, selectedSub, sortKey, sortAsc);

  const toggleSort = (key) => {
    if (sortKey === key) setSortAsc((v) => !v);
    else {
      setSortKey(key);
      setSortAsc(key === 'name');
    }
  };

  const sortMark = (key) => (sortKey === key ? (sortAsc ? ' ▲' : ' ▼') : '');

  const thStyle = { cursor: 'pointer', userSelect: 'none' };

  return (
    <section className="text-center d-flex" style={{ background: 'RGBA(0,0,0,0.9)' }}>

      {/* Background Video */}
      <div
        style={{
          backgroundImage: `URL(${dotPattern})`,
          backgroundRepeat: 'repeat',
          height: '100%',
          width: '100%',
          position: 'fixed',
          zIndex: '9',
        }}
      />
      <video autoPlay muted loop className="login-video">
        <source src={loginVideo} type="video/mp4" />
      </video>

      <div className="campaign-hub-container" style={{ position: 'relative', zIndex: 10, width: '100%' }}>
        <Link to="/">
          <img className="rounded position-relative" src={maxbountyLogoSquare} alt="" width="80" style={{ zIndex: 9 }} />
        </Link>
        <div className="position-relative" style={{ marginTop: '-40px' }}>
          <div className="m-3 pt-4 bg-white rounded pb-1">

            <h4 className="font-weight-bold mt-2">MaxBounty Campaign Hub</h4>

            {loading ? (
              <p className="text-muted py-4 mb-0"><Loading /></p>
            ) : error ? (
              <div className="alert alert-danger m-3 text-left" role="alert">
                {error}
              </div>
            ) : (
              <>
                <div className="d-flex justify-content-center mt-3 flex-wrap">
                  <select
                    className="form-control m-1"
                    style={{ maxWidth: '300px' }}
                    value={selectedMain}
                    onChange={(e) => {
                      setSelectedMain(e.target.value);
                      setSelectedSub('');
                    }}
                  >
                    <option value="">All Categories</option>
                    {mains.map((m) => (
                      <option key={m.main_category_id} value={String(m.main_category_id)}>
                        {m.main_category_name} ({m.count})
                      </option>
                    ))}
                  </select>
                  <select
                    className="form-control m-1"
                    style={{ maxWidth: '300px' }}
                    value={selectedSub}
                    onChange={(e) => setSelectedSub(e.target.value)}
                  >
                    <option value="">All Sub-Categories</option>
                    {subs.map((s) => (
                      <option key={s.subcategory_id} value={String(s.subcategory_id)}>
                        {s.subcategory_name} ({s.count})
                      </option>
                    ))}
                  </select>
                </div>

                <div className="d-flex justify-content-center mt-2">
                  <input
                    type="text"
                    className="form-control w-100 mx-2"
                    placeholder="Search campaigns..."
                    style={{ maxWidth: '610px' }}
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                </div>

                <div className="mt-3">
                  <table className="table table-striped table-mobile">
                    <thead>
                      <tr>
                        {/* <th width="50px"></th> */}
                        <th className="text-left" style={{...thStyle, width: "50%"}} onClick={() => toggleSort('name')}>
                          Campaign{sortMark('name')}
                        </th>
                        <th className="text-right" style={thStyle} onClick={() => toggleSort('rate')}>
                          <div className="px-4">
                            Rate{sortMark('rate')}
                          </div>
                        </th>
                        <th className="text-right" style={thStyle} onClick={() => toggleSort('epc')}>
                          EPC{sortMark('epc')}
                        </th>
                      </tr>
                    </thead>
                    <tbody className="text-left">
                      {rows.length === 0 ? (
                        <tr>
                          <td colSpan={3} className="text-center text-muted py-4">
                            No campaigns match your filters.
                          </td>
                        </tr>
                      ) : (
                        rows.map((c) => (
                          <tr key={c.offer_id}>
                            {/* <td width="50px">
                              {normalizeThumbnailUrl(c.thumbnail) ? (
                                <img
                                  src={normalizeThumbnailUrl(c.thumbnail)}
                                  width="50px"
                                  alt={`${c.name || 'Campaign'} thumbnail`}
                                />
                              ) : (
                                <span className="text-muted mr-2" style={{ display: 'inline-block', width: '50px' }}>
                                  —
                                </span>
                              )}{' '}
                            </td> */}
                            <td>                              
                              <Link target="_blank" rel="noopener noreferrer" to={`/campaignHub/${c.offer_id}`}>
                                {c.name}
                              </Link>
                            </td>
                            <td className="text-right">
                                {c.affiliate_rate_text}
                            </td>
                            <td className="text-right">{formatEpc(c.epc)}</td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

export default CampaignHub;
